// Catalogue autocomplete: curated suggestion templates (managed in the Intelligence studio) are filled from
// the customer's slot bag once per account, then matched against what the user types entirely in the
// browser — no keystroke waits on the network. Nothing matching means no suggestions (no model fallback).

const TYPED_SLOTS = ["AMOUNT", "DATE", "INVOICE", "MONTH", "COUNT", "FREQUENCY"];
const PLACEHOLDER = /\{([A-Z][A-Z0-9_]*)\}/g;

const STOP_WORDS = new Set([
  "a", "an", "the", "to", "for", "of", "on", "in", "at", "by", "and", "me", "my", "them", "their", "they",
  "it", "this", "that", "customer", "please", "pls", "plz", "can", "could", "would", "you", "i", "we", "us",
  "is", "be", "tell", "want", "need", "just", "also", "ah", "ok", "okay", "sorry", "actually", "now", "then",
  "put", "yes", "thanks", "hi",
]);

const WEEKDAYS = {
  monday: "Monday", mon: "Monday", tuesday: "Tuesday", tue: "Tuesday", tues: "Tuesday",
  wednesday: "Wednesday", wed: "Wednesday", thursday: "Thursday", thu: "Thursday", thur: "Thursday",
  thurs: "Thursday", friday: "Friday", fri: "Friday", saturday: "Saturday", sat: "Saturday",
  sunday: "Sunday", sun: "Sunday",
};
const MONTHS = {
  january: "January", jan: "January", february: "February", feb: "February", march: "March", mar: "March",
  april: "April", apr: "April", may: "May", june: "June", jun: "June", july: "July", jul: "July",
  august: "August", aug: "August", september: "September", sept: "September", sep: "September",
  october: "October", oct: "October", november: "November", nov: "November", december: "December",
  dec: "December",
};
// "may" is usually the verb; only read it as the month when the words around it say so.
const MAY_CONTEXT = new Set(["in", "for", "of", "from", "created", "due", "dated", "raised", "invoices", "invs", "balance"]);
const FREQUENCIES = { weekly: "weekly", monthly: "monthly", daily: "daily", fortnightly: "fortnightly" };
const PLAN_UNITS = { week: "weekly", weeks: "weekly", month: "monthly", months: "monthly", day: "daily", days: "daily" };

// @-mentions arrive as <colleague>Name</colleague> markup; they say who to tell, not what to do.
const stripMentions = (text) => text.replace(/<colleague>.*?<\/colleague>/gi, " ");
const words = (text) => stripMentions(text).toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);

// Fill the customer's slots and drop templates the customer isn't eligible for. Typed slots ({AMOUNT},
// {DATE}, {INVOICE}, {MONTH}, {COUNT}, {FREQUENCY}) stay as placeholders — only what the user types can
// fill them.
export function renderCatalogue(catalogue, slotBag) {
  if (!catalogue?.suggestions?.length || !slotBag) return [];
  const conditions = new Set(slotBag.conditions ?? []);
  const slots = slotBag.slots ?? {};

  return catalogue.suggestions.flatMap((suggestion, order) => {
    if (!(suggestion.conditions ?? []).every((condition) => conditions.has(condition))) return [];
    let missing = false;
    const typed = new Set();
    const text = suggestion.template.replace(PLACEHOLDER, (match, name) => {
      if (TYPED_SLOTS.includes(name)) {
        typed.add(name);
        return match;
      }
      if (slots[name] == null) missing = true;
      return slots[name] ?? match;
    });
    if (missing) return [];
    const aliases = (suggestion.aliases ?? []).map((alias) => alias.toLowerCase());
    return [{
      id: suggestion.id,
      kind: suggestion.kind,
      text,
      typed: [...typed],
      aliases,
      aliasWords: aliases.flatMap(words),
      prior: suggestion.prior ?? 0,
      basePriority: suggestion.basePriority ?? 0,
      order,
    }];
  });
}

// Pull values the user typed that a template's typed slot can carry, and the words they occupy (which are
// then not used for matching): "pr £500 on friday" → { AMOUNT: "£500", DATE: "Friday" }, "pp 4x250 weekly"
// → { COUNT: "4", AMOUNT: "£250", FREQUENCY: "weekly" }, "copies of aug invs" → { MONTH: "August" }.
export function extractTypedValues(rawText) {
  const text = stripMentions(rawText);
  const values = {};
  const consumed = new Set();
  const consume = (fragment) => words(fragment).forEach((w) => consumed.add(w));
  const lower = text.toLowerCase();

  const times = lower.match(/\b(\d{1,2})\s*x\s*([£€$])?\s?(\d[\d,]*(?:\.\d{1,2})?)\b/);
  if (times) {
    values.COUNT = times[1];
    values.AMOUNT = `${times[2] ?? "£"}${times[3]}`;
    consume(times[0]);
  } else {
    const amount = text.match(/([£€$])\s?(\d[\d,]*(?:\.\d{1,2})?)/);
    if (amount) {
      values.AMOUNT = `${amount[1]}${amount[2]}`;
      consume(amount[0]);
    }
  }

  const invoice = text.match(/\b([a-z]{1,6}-?\d{3,})\b/i) ?? text.match(/\b(\d{5,})\b/);
  if (invoice) {
    values.INVOICE = invoice[1].toUpperCase();
    consume(invoice[0]);
  }

  const tokens = words(lower);
  const isMonth = (word, index) =>
    MONTHS[word] && (word !== "may" || MAY_CONTEXT.has(tokens[index - 1]) || MAY_CONTEXT.has(tokens[index + 1]));

  const dayMonth = lower.match(/\b(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?([a-z]{3,9})\b/);
  const monthDay = lower.match(/\b([a-z]{3,9})\s+(\d{1,2})(?:st|nd|rd|th)?\b/);
  const ordinal = lower.match(/\b(\d{1,2})(st|nd|rd|th)\b/);
  const numeric = lower.match(/\b(\d{1,2})[./](\d{1,2})(?:[./](\d{2,4}))?\b/);
  const numericMonth = numeric && !consumed.has(numeric[1]) && Object.values(MONTHS).filter((m, i, all) => all.indexOf(m) === i)[Number(numeric[2]) - 1];
  const weekday = tokens.find((w) => WEEKDAYS[w]);
  if (dayMonth && MONTHS[dayMonth[2]]) {
    values.DATE = `${Number(dayMonth[1])} ${MONTHS[dayMonth[2]]}`;
    consume(dayMonth[0]);
  } else if (monthDay && MONTHS[monthDay[1]] && monthDay[1] !== "may") {
    values.DATE = `${Number(monthDay[2])} ${MONTHS[monthDay[1]]}`;
    consume(monthDay[0]);
  } else if (numericMonth && Number(numeric[1]) >= 1 && Number(numeric[1]) <= 31) {
    values.DATE = `${Number(numeric[1])} ${numericMonth}`;
    consume(numeric[0]);
  } else if (ordinal) {
    values.DATE = `the ${ordinal[1]}${ordinal[2]}`;
    consumed.add(`${ordinal[1]}${ordinal[2]}`);
  } else if (/\btomorrow\b/.test(lower)) {
    values.DATE = "tomorrow";
    consumed.add("tomorrow");
  } else if (weekday) {
    values.DATE = WEEKDAYS[weekday];
    consumed.add(weekday);
  }

  const monthIndex = tokens.findIndex((w, i) => !consumed.has(w) && isMonth(w, i));
  if (monthIndex >= 0) {
    values.MONTH = MONTHS[tokens[monthIndex]];
    consumed.add(tokens[monthIndex]);
  }

  const frequency = tokens.find((w) => FREQUENCIES[w]);
  const planCue = /\b(pp|plan|instal|install)/.test(lower);
  const unit = planCue && tokens.find((w) => PLAN_UNITS[w]);
  if (frequency) {
    values.FREQUENCY = FREQUENCIES[frequency];
    consumed.add(frequency);
  } else if (unit) {
    values.FREQUENCY = PLAN_UNITS[unit];
    consumed.add(unit);
  }

  // Years ("from january 2026") are context, not a count.
  const count = values.COUNT ? null : tokens.find((w) => !consumed.has(w) && /^\d{1,2}$/.test(w));
  if (count) {
    values.COUNT = String(Number(count));
    consumed.add(count);
  }
  tokens.filter((w) => /^(19|20)\d{2}$/.test(w)).forEach((w) => consumed.add(w));

  return { values, consumed };
}

// One insertion, deletion, substitution or adjacent swap ("sned" → "send", "invocies" → "invoices").
function withinOneEdit(a, b) {
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    const diff = [...a].map((ch, i) => (ch === b[i] ? -1 : i)).filter((i) => i >= 0);
    if (diff.length === 2 && diff[1] === diff[0] + 1 && a[diff[0]] === b[diff[1]] && a[diff[1]] === b[diff[0]])
      return true;
  }
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

// Exact alias > exact word > prefix (the word being typed) > a one-letter typo on a longer word.
function tokenScore(token, candidateWords, aliasWords, aliases) {
  if (aliases.includes(token) || aliasWords.includes(token)) return 3;
  if (candidateWords.includes(token)) return 2;
  if (candidateWords.some((w) => w.startsWith(token)) || aliasWords.some((w) => w.startsWith(token))) return 1;
  if (token.length >= 4 && [...candidateWords, ...aliasWords].some((w) => w.length >= 4 && withinOneEdit(token, w)))
    return 0.5;
  return 0;
}

const byRank = (a, b) =>
  b.score - a.score || b.prior - a.prior || b.basePriority - a.basePriority || a.order - b.order;

export function matchCatalogue(candidates, text, limit = 3) {
  if (!candidates?.length) return [];

  if (!text.trim()) {
    return candidates
      .filter((c) => c.typed.length === 0)
      .map((c) => ({ ...c, score: 0 }))
      .sort(byRank)
      .slice(0, limit)
      .map(toSuggestion);
  }

  const { values, consumed } = extractTypedValues(text);
  const typedNames = Object.keys(values);
  const tokens = words(text).filter((t) => !STOP_WORDS.has(t) && !consumed.has(t));

  return candidates
    .map((candidate) => {
      // A typed value the template can't carry would be silently dropped ("pr £500" must not become a
      // request for the full balance), and a typed slot nothing filled can't render.
      const carriedElsewhere = (name) => words(values[name]).every((w) => words(candidate.text).includes(w));
      if (typedNames.some((name) => !candidate.typed.includes(name) && !carriedElsewhere(name))) return null;
      if (candidate.typed.some((name) => values[name] == null)) return null;
      const filled = candidate.text.replace(PLACEHOLDER, (match, name) => values[name] ?? match);
      const candidateWords = words(filled);
      let score = typedNames.length;
      for (const token of tokens) {
        const hit = tokenScore(token, candidateWords, candidate.aliasWords, candidate.aliases);
        if (hit === 0) return null;
        score += hit;
      }
      if (tokens.length === 0 && typedNames.length === 0) return null;
      return { ...candidate, text: filled, score };
    })
    .filter(Boolean)
    .sort(byRank)
    .slice(0, limit)
    .map(toSuggestion);
}

const toSuggestion = (c) => ({ id: c.id, promptText: c.text, kind: c.kind, source: "catalogue" });
